import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { AuthBackdrop } from "./AuthBackdrop";

interface AuthShellProps {
  /** Bandeau festif (signup) ou sobre (login/forgot/employee-setup). */
  intensity?: "festive" | "sober";
  /** Layout split (signup desktop) ou centered (login, etc.). */
  layout?: "centered" | "split";
  /** Colonne gauche en mode split (panneau de bénéfices). */
  aside?: ReactNode;
  children: ReactNode;
}

export function AuthShell({ intensity = "sober", layout = "centered", aside, children }: AuthShellProps) {
  const split = layout === "split" && aside;

  // `translate="no"` : Google Traduction réécrit les nœuds de texte du DOM
  // (balises <font>) et fait échouer les insertions de React. Cf. le commentaire
  // détaillé dans src/components/ui/button.tsx.
  return (
    <div className="relative min-h-screen bg-background text-foreground" translate="no">
      <AuthBackdrop intensity={intensity} />

      {/* Header navigation : retour + logo */}
      <header className="relative z-10 flex items-center justify-between px-4 sm:px-6 lg:px-10 pt-5 lg:pt-7">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-fast rounded-md px-1.5 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Retour à l'accueil</span>
          <span className="sm:hidden">Accueil</span>
        </Link>

        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-label="Equiply — Retour à l'accueil"
        >
          <img src="/logo.webp" alt="" width={28} height={28} decoding="async" className="w-7 h-7 object-contain" />
          <span className="text-base font-bold tracking-tight text-foreground">Equiply</span>
        </Link>
      </header>

      <main
        className={cn(
          "relative z-10 px-4 sm:px-6 lg:px-10 pb-12 pt-6 lg:pt-10",
          split
            ? "flex flex-col lg:grid lg:grid-cols-[1.05fr_1fr] xl:grid-cols-[1.1fr_1fr] lg:gap-12 xl:gap-16 lg:items-center lg:max-w-7xl lg:mx-auto"
            : "flex flex-col items-center justify-center min-h-[calc(100vh-5rem)]",
        )}
      >
        {/* Sur mobile, le formulaire passe en premier : le panneau d'arguments le suivait avec un
            second grand titre et repoussait le premier champ sous la ligne de flottaison. */}
        {split && (
          <aside className="order-last mt-10 lg:order-none lg:mt-0">
            {aside}
          </aside>
        )}
        <section className={cn("w-full", split ? "mx-auto lg:max-w-md" : "max-w-md")}>{children}</section>
      </main>
    </div>
  );
}
